/* =============================================================
   StudentSettings.jsx — Student portal settings
   -------------------------------------------------------------
   Allows the student to update account info, notification
   preferences, and privacy settings.
   Changes are saved to localStorage for the demo.

   TODO (backend integration):
   - Replace localStorage with a Supabase profile update
   ============================================================= */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StudentLayout from '../../components/StudentLayout';
import { useAuth } from '../../context/AuthContext';

/* ─── DEFAULTS ──────────────────────────────────────────── */
const ACCOUNT_DEFAULTS = {
  fullName: 'Tom Baker',
  email:    't.baker@esmt.org',
};
const NOTIF_DEFAULTS = {
  deadlineReminders: true,
  documentUpdates:   true,
  learningAgreement: true,
  announcements:     false,
};

/* ─── HELPERS ───────────────────────────────────────────── */
function load(key, defaults) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...defaults, ...JSON.parse(raw) } : defaults;
  } catch { return defaults; }
}
function persist(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

/* ─── SUB-COMPONENTS ────────────────────────────────────── */
function SaveBtn({ onClick, saved }) {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
        saved
          ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
          : 'bg-[#4343d5] text-white hover:bg-indigo-700'
      }`}
    >
      <span className="material-symbols-outlined text-[16px]">
        {saved ? 'check_circle' : 'save'}
      </span>
      {saved ? 'Saved!' : 'Save Changes'}
    </button>
  );
}

function Toggle({ on, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`relative inline-flex items-center w-11 h-6 rounded-full flex-shrink-0 transition-colors duration-200 focus:outline-none ${on ? 'bg-indigo-600' : 'bg-slate-200'}`}
    >
      <span className={`inline-block w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

/* ─── STUDENT SETTINGS COMPONENT ───────────────────────── */
export default function StudentSettings() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [account,      setAccount]      = useState(() => load('unixplore_student_account', ACCOUNT_DEFAULTS));
  const [accountSaved, setAccountSaved] = useState(false);
  const [newPass,      setNewPass]      = useState('');
  const [confirmPass,  setConfirmPass]  = useState('');
  const [passError,    setPassError]    = useState('');

  const [notif, setNotif] = useState(() => load('unixplore_student_notif', NOTIF_DEFAULTS));

  const flash = (setter) => { setter(true); setTimeout(() => setter(false), 2000); };

  const saveAccount = () => {
    if (newPass || confirmPass) {
      if (newPass !== confirmPass) { setPassError('Passwords do not match.'); return; }
      if (newPass.length < 6)      { setPassError('Password must be at least 6 characters.'); return; }
      setPassError('');
      setNewPass(''); setConfirmPass('');
    }
    persist('unixplore_student_account', account);
    flash(setAccountSaved);
  };

  const toggleNotif = (key) => {
    const updated = { ...notif, [key]: !notif[key] };
    setNotif(updated);
    persist('unixplore_student_notif', updated);
  };

  const resetPrototype = () => {
    if (!window.confirm('This will erase all saved data and log you out. Continue?')) return;
    localStorage.clear();
    logout();
    navigate('/login');
  };

  const notifItems = [
    { key: 'deadlineReminders', label: 'Deadline reminders',          desc: '7 days before each document deadline'                       },
    { key: 'documentUpdates',   label: 'Document status updates',     desc: 'When your IO reviews or approves a document'                },
    { key: 'learningAgreement', label: 'Learning Agreement updates',  desc: 'When your agreement is signed or requires action'           },
    { key: 'announcements',     label: 'General announcements',       desc: 'News from ESMT International Office'                        },
  ];

  return (
    <StudentLayout>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">Settings</h1>
        <p className="text-[#464555] mt-1">Manage your account preferences and notifications.</p>
      </div>

      <div className="grid grid-cols-12 gap-6">

        {/* Left column */}
        <div className="col-span-8 space-y-6">

          {/* Account Settings */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">manage_accounts</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Account Settings</h2>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={account.fullName}
                    onChange={e => setAccount(v => ({ ...v, fullName: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Email</label>
                  <input
                    type="email"
                    value={account.email}
                    onChange={e => setAccount(v => ({ ...v, email: e.target.value }))}
                    className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="pt-2 border-t border-slate-100">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Change Password</p>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs text-slate-400 block mb-1.5">New Password</label>
                    <input
                      type="password"
                      value={newPass}
                      onChange={e => { setNewPass(e.target.value); setPassError(''); }}
                      placeholder="••••••••"
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1.5">Confirm Password</label>
                    <input
                      type="password"
                      value={confirmPass}
                      onChange={e => { setConfirmPass(e.target.value); setPassError(''); }}
                      placeholder="••••••••"
                      className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] transition"
                    />
                  </div>
                </div>
                {passError && (
                  <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                    <span className="material-symbols-outlined text-[13px]">error</span>
                    {passError}
                  </p>
                )}
              </div>

              <div className="flex justify-end">
                <SaveBtn onClick={saveAccount} saved={accountSaved} />
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">notifications</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Notifications</h2>
              </div>
              <span className="text-xs text-slate-400">Changes save automatically</span>
            </div>
            <div className="p-6 space-y-4">
              {notifItems.map(item => (
                <div key={item.key} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-[#0b1c30]">{item.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{item.desc}</p>
                  </div>
                  <Toggle on={notif[item.key]} onToggle={() => toggleNotif(item.key)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="col-span-4 space-y-6">

          {/* Profile card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Profile</h2>
            </div>
            <div className="p-6 flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-sky-500 flex items-center justify-center text-white text-2xl font-bold font-display">
                {account.fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-[#0b1c30]">{account.fullName}</p>
                <p className="text-xs text-slate-400 mt-0.5">MIM · Exchange 2026/27</p>
                <p className="text-xs text-slate-400 mt-0.5">{account.email}</p>
              </div>
            </div>
          </div>

          {/* Sign out */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Session</h2>
            </div>
            <div className="p-5 space-y-3">
              <button
                onClick={() => { logout(); navigate('/login'); }}
                className="w-full flex items-center justify-center gap-2 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
              >
                <span className="material-symbols-outlined text-[16px]">logout</span>
                Sign Out
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <p className="text-xs text-slate-300 text-center py-8">Unixplore © 2026 · All rights reserved</p>
    </StudentLayout>
  );
}
